import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import SuccessModal from './SuccessModal';

const ECGFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SectionsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 1024px) {
    flex-direction: row;
  }
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 12px;
  width: 100%;

  @media (min-width: 1024px) {
    width: calc(50% - 12px);
  }
`;

const SectionTitle = styled.h2`
  width: 100%;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: #6D6D71;
`;

const ContentBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #C5C5C6;
  border-radius: 12px;
  padding: 16px;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: calc(50% - 10px);
  min-width: 150px;
  flex: 1;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const FormLabel = styled.label`
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
`;

const FormInput = styled.input`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  height: 36px;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;

  &::placeholder {
    color: #A7A7AA;
  }
`;

const FormTextarea = styled.textarea`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
  min-height: 80px;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
  resize: vertical;

  &::placeholder {
    color: #A7A7AA;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
`;

const RadioInput = styled.input`
  width: 16px;
  height: 16px;
`;

const SaveButton = styled.button`
  display: flex;
  width: 230px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 15px 15px;
  gap: 12px;
  background: #2575BD;
  border: none;
  border-radius: 16px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  text-align: center;
  color: #FFFFFF;
  cursor: pointer;
  margin-top: 20px;
  &:hover {
    background-color: ${props => props.disabled ? '#E0F0FF' : '#1c5c94'};
  }
`;

const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid #E2E2E3;
  margin: 16px 0;
`;

const LVHContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const LVHFieldsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const LVHField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 120px;
`;

const WaveGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 12px;
  background: #F9F9F9;
  border-radius: 8px;
`;

const WaveGroupTitle = styled.h3`
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 13px;
  line-height: 16px;
  color: #6D6D71;
  margin: 0;
`;

const WaveFieldsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const STSegmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 12px;
  background: #F9F9F9;
  border-radius: 8px;
`;

const SelectInput = styled.select`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  height: 36px;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
  cursor: pointer;

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  height: 36px;
  background: #FFFFFF;
  border: 1px solid #2575BD;
  border-radius: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: #2575BD;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #E0F0FF;
  }

  &:active {
    background: #C5E3FF;
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #FFFFFF;
  border: 1px solid #DC3545;
  border-radius: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  color: #DC3545;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #FFE5E8;
  }

  &:active {
    background: #FFCCD2;
  }
`;

const STSegmentEntry = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  padding: 12px;
  background: #FFFFFF;
  border: 1px solid #E2E2E3;
  border-radius: 8px;
`;

const ECGForm = ({ participantId, formData, onFormDataChange, onSaveSuccess }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [data, setData] = useState({
    basic_measurements: {},
    wave_amplitudes: {},
    st_segments: [
      {
        id: Date.now(),
        lead: '',
        status: '',
        measurement: ''
      }
    ],
    criteria: {
      left_atrial_enlargement: '',
      left_ventricular_hypertrophy: '',
      right_ventricular_hypertrophy: '',
      lvh_rohmilt_estes: '',
      lvh_sokolov_lyon: ''
    },
    remark_left_atrial_enlargement: '',
    remark_right_ventricular_hypertrophy: '',
    remark_left_ventricular_hypertrophy: '',
    rhythm_and_conduction: {
      arrhythmias: {},
      bundle_branch_block: {},
      other_findings: {}
    },
    additional_remarks: ''
  });

  useEffect(() => {
    const fetchECGData = async () => {
      try {
        // Verify participantId exists and is valid
        if (!participantId || typeof participantId !== 'string') {
          console.error('Invalid participant ID:', participantId);
          return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No authentication token found');
          return;
        }

        // Construct the URL with proper encoding
        const url = new URL(
          `${process.env.REACT_APP_BACKEND_URL}participant/get-participant-info`
        );
        url.searchParams.append('participant_id', participantId);

        const response = await axios.get(url.toString(), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Check if response contains valid data
        if (!response.data) {
          console.error('No data received from the server');
          return;
        }

        const ecgData = response.data?.ecg || {};
        
        // Handle ST segments - convert old format to new array format if needed
        let stSegments = [];
        if (ecgData.st_segments && Array.isArray(ecgData.st_segments) && ecgData.st_segments.length > 0) {
          stSegments = ecgData.st_segments.map((seg, index) => ({
            id: seg.id || Date.now() + index,
            lead: seg.lead || '',
            status: seg.status || '',
            measurement: seg.measurement || ''
          }));
        } else if (ecgData.st_segment) {
          // Legacy format - single ST segment object
          stSegments = [{
            id: Date.now(),
            lead: ecgData.st_segment.lead || '',
            status: ecgData.st_segment.status || '',
            measurement: ecgData.st_segment.measurement || ''
          }];
        } else {
          // Default - one empty entry
          stSegments = [{
            id: Date.now(),
            lead: '',
            status: '',
            measurement: ''
          }];
        }
        
        setData({
          basic_measurements: ecgData.basic_measurements || {},
          wave_amplitudes: ecgData.wave_amplitudes || {},
          st_segments: stSegments,
          criteria: {
            left_atrial_enlargement: ecgData.criteria?.left_atrial_enlargement || '',
            left_ventricular_hypertrophy: ecgData.criteria?.left_ventricular_hypertrophy || '',
            right_ventricular_hypertrophy: ecgData.criteria?.right_ventricular_hypertrophy || '',
            lvh_rohmilt_estes: ecgData.criteria?.lvh_rohmilt_estes || '',
            lvh_sokolov_lyon: ecgData.criteria?.lvh_sokolov_lyon || ''
          },
          remark_left_atrial_enlargement: ecgData.remark_left_atrial_enlargement || '',
          remark_right_ventricular_hypertrophy: ecgData.remark_right_ventricular_hypertrophy || '',
          remark_left_ventricular_hypertrophy: ecgData.remark_left_ventricular_hypertrophy || '',
          rhythm_and_conduction: {
            arrhythmias: ecgData.rhythm_and_conduction?.arrhythmias || {},
            heart_block: ecgData.rhythm_and_conduction?.heart_block || '',
            bundle_branch_block: ecgData.rhythm_and_conduction?.bundle_branch_block || {},
            other_findings: ecgData.rhythm_and_conduction?.other_findings || {}
          },
          additional_remarks: ecgData.additional_remarks || ''
        });
        onFormDataChange(ecgData);
      } catch (error) {
        console.error('Error fetching ECG data:', error);
        if (error.response) {
          console.error('Response data:', error.response.data);
          console.error('Response status:', error.response.status);
          console.error('Response headers:', error.response.headers);
        } else if (error.request) {
          console.error('No response received:', error.request);
        } else {
          console.error('Error setting up request:', error.message);
        }
      }
    };

    fetchECGData();
  }, [participantId]);

  const handleInputChange = (section, field, value) => {
    const updatedData = {
      ...data,
      [section]: {
        ...data[section],
        [field]: value
      }
    };
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  const handleNestedInputChange = (section, subSection, field, value) => {
    const updatedData = {
      ...data,
      [section]: {
        ...data[section],
        [subSection]: {
          ...data[section][subSection],
          [field]: value
        }
      }
    };
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  // ST Segment handlers
  const handleSTSegmentChange = (id, field, value) => {
    const updatedSegments = data.st_segments.map(segment =>
      segment.id === id ? { ...segment, [field]: value } : segment
    );
    const updatedData = {
      ...data,
      st_segments: updatedSegments
    };
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  const addSTSegment = () => {
    const updatedData = {
      ...data,
      st_segments: [
        ...data.st_segments,
        {
          id: Date.now(),
          lead: '',
          status: '',
          measurement: ''
        }
      ]
    };
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  const removeSTSegment = (id) => {
    // Keep at least one ST segment entry
    if (data.st_segments.length <= 1) return;
    
    const updatedData = {
      ...data,
      st_segments: data.st_segments.filter(segment => segment.id !== id)
    };
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  // Calculate R/S ratio automatically for V1
  const calculateRSRatioV1 = () => {
    const rV1 = parseFloat(data.wave_amplitudes?.r_wave_amplitude_v1) || 0;
    const sV1 = parseFloat(data.wave_amplitudes?.s_wave_amplitude_v1) || 0;
    
    if (sV1 === 0) return '';
    
    const ratio = rV1 / sV1;
    return ratio.toFixed(2);
  };

  // Calculate R/S ratio automatically for V5
  const calculateRSRatioV5 = () => {
    const rV5 = parseFloat(data.wave_amplitudes?.r_wave_amplitude_v5) || 0;
    const sV5 = parseFloat(data.wave_amplitudes?.s_wave_amplitude_v5) || 0;
    
    if (sV5 === 0) return '';
    
    const ratio = rV5 / sV5;
    return ratio.toFixed(2);
  };

  const renderTextField = (path, field, label, unit, isReadOnly = false, calculatedValue = null, inputType = 'number') => {
    let value;
    if (calculatedValue !== null) {
      value = calculatedValue;
    } else if (path.includes('.')) {
      // Handle deeply nested paths
      const pathParts = path.split('.');
      let current = data;
      for (const part of pathParts) {
        current = current?.[part];
      }
      value = current?.[field] ?? '';
    } else {
      value = data[path]?.[field] ?? '';
    }

    return (
      <FormField>
        <FormLabel>{label} {unit && `(${unit})`}</FormLabel>
        <FormInput
          type={inputType}
          value={value}
          onChange={(e) => {
            if (isReadOnly) return;
            if (path.includes('.')) {
              const pathParts = path.split('.');
              if (pathParts.length === 2) {
                handleNestedInputChange(pathParts[0], pathParts[1], field, e.target.value);
              } else if (pathParts.length === 3) {
                // Handle three-level nesting
                const updatedData = {
                  ...data,
                  [pathParts[0]]: {
                    ...data[pathParts[0]],
                    [pathParts[1]]: {
                      ...data[pathParts[0]][pathParts[1]],
                      [field]: e.target.value
                    }
                  }
                };
                setData(updatedData);
                onFormDataChange(updatedData);
              }
            } else {
              handleInputChange(path, field, e.target.value);
            }
          }}
          placeholder={isReadOnly ? 'Auto-calculated' : `Enter ${label.toLowerCase()}`}
          readOnly={isReadOnly}
          style={isReadOnly ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
        />
      </FormField>
    );
  };

  const renderRadioGroup = (section, field, options, label) => (
    <FormField>
      <FormLabel>{label}</FormLabel>
      <RadioGroup>
        {options.map((option) => (
          <RadioLabel key={option}>
            <RadioInput
              type="radio"
              name={`${section}-${field}`}
              value={option}
              checked={data[section][field] === option}
              onChange={() => handleInputChange(section, field, option)}
            />
            {option}
          </RadioLabel>
        ))}
      </RadioGroup>
    </FormField>
  );

  const renderSelectField = (section, field, label, options) => (
    <FormField>
      <FormLabel>{label}</FormLabel>
      <SelectInput
        value={data[section]?.[field] || ''}
        onChange={(e) => handleInputChange(section, field, e.target.value)}
      >
        <option value="">Select {label}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </SelectInput>
    </FormField>
  );

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      // Include calculated R/S ratios in the data
      const dataToSave = {
        ...data,
        wave_amplitudes: {
          ...data.wave_amplitudes,
          rs_ratio_v1: calculateRSRatioV1(),
          rs_ratio_v5: calculateRSRatioV5()
        }
      };

      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}participant/update-ecg`,
        {
          participant_id: participantId,
          ecg: dataToSave  // Nest all ECG data inside an 'ecg' object
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      console.log('Save successful:', response.data);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error saving ECG form:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Call onSaveSuccess here instead
    onSaveSuccess();
  };

  return (
    <ECGFormContainer>
      <SectionsRow>
        <SectionContainer>
          <SectionTitle>BASIC MEASUREMENTS & WAVE AMPLITUDES</SectionTitle>
          <ContentBox>
            <FieldsContainer>
              <SectionTitle>Basic Measurements</SectionTitle>
              {renderTextField('basic_measurements', 'heart_rate', 'Heart Rate', 'bpm')}
              {renderTextField('basic_measurements', 'pr_interval', 'PR Interval', 'ms')}
              {renderTextField('basic_measurements', 'qrs_duration', 'QRS Duration', 'ms')}
              {renderTextField('basic_measurements', 'qt_interval', 'QT Interval', 'ms')}
              {renderTextField('basic_measurements', 'qtc_interval', 'QTc Interval', 'ms')}
              {renderTextField('basic_measurements', 'p_wave_duration', 'P Wave Duration', 'ms')}
              {renderTextField('basic_measurements', 'p_wave_amplitude', 'P Wave Amplitude', 'mV')}
              {renderTextField('basic_measurements', 'p_wave_axis', 'P Wave Axis', '°')}
              {renderTextField('basic_measurements', 'qrs_axis', 'QRS Axis', '°')}
              {renderTextField('basic_measurements', 't_wave_axis', 'T Wave Axis', '°')}
            </FieldsContainer>
            <Divider />
            <FieldsContainer>
              <SectionTitle>Wave Amplitudes</SectionTitle>
              <WaveGroup>
                <WaveGroupTitle>Lead V1</WaveGroupTitle>
                <WaveFieldsRow>
                  {renderTextField('wave_amplitudes', 'r_wave_amplitude_v1', 'R Wave Amplitude', 'mV')}
                  {renderTextField('wave_amplitudes', 's_wave_amplitude_v1', 'S Wave Amplitude', 'mV')}
                  {renderTextField('wave_amplitudes', 'rs_ratio_v1', 'R/S Ratio', '', true, calculateRSRatioV1())}
                </WaveFieldsRow>
              </WaveGroup>
              <WaveGroup>
                <WaveGroupTitle>Lead V5</WaveGroupTitle>
                <WaveFieldsRow>
                  {renderTextField('wave_amplitudes', 'r_wave_amplitude_v5', 'R Wave Amplitude', 'mV')}
                  {renderTextField('wave_amplitudes', 's_wave_amplitude_v5', 'S Wave Amplitude', 'mV')}
                  {renderTextField('wave_amplitudes', 'rs_ratio_v5', 'R/S Ratio', '', true, calculateRSRatioV5())}
                </WaveFieldsRow>
              </WaveGroup>
              <STSegmentContainer>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <WaveGroupTitle>ST Segment Analysis</WaveGroupTitle>
                  <AddButton onClick={addSTSegment}>
                    + Add Lead
                  </AddButton>
                </div>
                {data.st_segments?.map((segment, index) => (
                  <STSegmentEntry key={segment.id}>
                    <FormField>
                      <FormLabel>Lead</FormLabel>
                      <SelectInput
                        value={segment.lead || ''}
                        onChange={(e) => handleSTSegmentChange(segment.id, 'lead', e.target.value)}
                      >
                        <option value="">Select Lead</option>
                        <option value="I">I</option>
                        <option value="II">II</option>
                        <option value="III">III</option>
                        <option value="aVR">aVR</option>
                        <option value="aVL">aVL</option>
                        <option value="aVF">aVF</option>
                        <option value="V1">V1</option>
                        <option value="V2">V2</option>
                        <option value="V3">V3</option>
                        <option value="V4">V4</option>
                        <option value="V5">V5</option>
                        <option value="V6">V6</option>
                      </SelectInput>
                    </FormField>
                    <FormField>
                      <FormLabel>Status</FormLabel>
                      <SelectInput
                        value={segment.status || ''}
                        onChange={(e) => handleSTSegmentChange(segment.id, 'status', e.target.value)}
                      >
                        <option value="">Select Status</option>
                        <option value="Normal">Normal</option>
                        <option value="Elevated">Elevated</option>
                        <option value="Depressed">Depressed</option>
                      </SelectInput>
                    </FormField>
                    {segment.status && segment.status !== 'Normal' && (
                      <FormField>
                        <FormLabel>Measurement (mV)</FormLabel>
                        <FormInput
                          type="number"
                          step="0.1"
                          value={segment.measurement || ''}
                          onChange={(e) => handleSTSegmentChange(segment.id, 'measurement', e.target.value)}
                          placeholder="Enter measurement"
                        />
                      </FormField>
                    )}
                    {data.st_segments.length > 1 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <FormLabel style={{ opacity: 0 }}>Remove</FormLabel>
                        <RemoveButton onClick={() => removeSTSegment(segment.id)} title="Remove this lead">
                          ×
                        </RemoveButton>
                      </div>
                    )}
                  </STSegmentEntry>
                ))}
              </STSegmentContainer>
            </FieldsContainer>
          </ContentBox>
        </SectionContainer>

        <SectionContainer>
          <SectionTitle>CRITERIA & RHYTHM AND CONDUCTION</SectionTitle>
          <ContentBox>
            <SectionTitle>Criteria</SectionTitle>
            <FieldsContainer>
              {renderSelectField('criteria', 'left_atrial_enlargement', 'Left Atrial Enlargement', ['Present', 'Absent'])}
              {renderTextField('remark_left_atrial_enlargement', 'remark_left_atrial_enlargement', 'Remark', '', false, null, 'text')}
            </FieldsContainer>
            <FieldsContainer>
              {renderSelectField('criteria', 'right_ventricular_hypertrophy', 'Right Ventricular Hypertrophy', ['Present', 'Absent'])}
              {renderTextField('remark_right_ventricular_hypertrophy', 'remark_right_ventricular_hypertrophy', 'Remark', '', false, null, 'text')}
            </FieldsContainer>
            <FieldsContainer>
              {renderSelectField('criteria', 'left_ventricular_hypertrophy', 'Left Ventricular Hypertrophy', ['Present', 'Absent'])}
              {renderTextField('remark_left_ventricular_hypertrophy', 'remark_left_ventricular_hypertrophy', 'Remark', '', false, null, 'text')}
            </FieldsContainer>
            <FieldsContainer>
              {renderSelectField('criteria', 'lvh_rohmilt_estes', 'LVH - Rohmilt-Estes Criteria', ['Definite', 'No', 'Probable'])}
              {renderSelectField('criteria', 'lvh_sokolov_lyon', 'LVH - Sokolov-Lyon Criteria', ['Present', 'Absent'])}
            </FieldsContainer>
            <Divider />
            <FieldsContainer>
              <SectionTitle>Rhythm and Conduction</SectionTitle>
              <FormField>
                <FormLabel>Arrhythmias</FormLabel>
                <RadioGroup>
                  <RadioLabel>
                    <RadioInput
                      type="checkbox"
                      checked={data.rhythm_and_conduction.arrhythmias.atrial_fibrillation}
                      onChange={(e) => handleNestedInputChange('rhythm_and_conduction', 'arrhythmias', 'atrial_fibrillation', e.target.checked)}
                    />
                    Atrial Fibrillation
                  </RadioLabel>
                  <RadioLabel>
                    <RadioInput
                      type="checkbox"
                      checked={data.rhythm_and_conduction.arrhythmias.atrial_flutter}
                      onChange={(e) => handleNestedInputChange('rhythm_and_conduction', 'arrhythmias', 'atrial_flutter', e.target.checked)}
                    />
                    Atrial Flutter
                  </RadioLabel>
                </RadioGroup>
              </FormField>
              {renderTextField('rhythm_and_conduction.arrhythmias', 'pvc_count', 'PVCs (count/min)', '')}
              {renderRadioGroup(
                'rhythm_and_conduction',
                'heart_block',
                ['Type I', 'Type IIA', 'Type IIB', 'Type III'],
                'Heart Block'
              )}
              <FormField>
                <FormLabel>Bundle Branch Block</FormLabel>
                <RadioGroup>
                  <RadioLabel>
                    <RadioInput
                      type="checkbox"
                      checked={data.rhythm_and_conduction.bundle_branch_block.complete_lbbb}
                      onChange={(e) => handleNestedInputChange('rhythm_and_conduction', 'bundle_branch_block', 'complete_lbbb', e.target.checked)}
                    />
                    Complete LBBB
                  </RadioLabel>
                  <RadioLabel>
                    <RadioInput
                      type="checkbox"
                      checked={data.rhythm_and_conduction.bundle_branch_block.incomplete_lbbb}
                      onChange={(e) => handleNestedInputChange('rhythm_and_conduction', 'bundle_branch_block', 'incomplete_lbbb', e.target.checked)}
                    />
                    Incomplete LBBB
                  </RadioLabel>
                  <RadioLabel>
                    <RadioInput
                      type="checkbox"
                      checked={data.rhythm_and_conduction.bundle_branch_block.complete_rbbb}
                      onChange={(e) => handleNestedInputChange('rhythm_and_conduction', 'bundle_branch_block', 'complete_rbbb', e.target.checked)}
                    />
                    Complete RBBB
                  </RadioLabel>
                  <RadioLabel>
                    <RadioInput
                      type="checkbox"
                      checked={data.rhythm_and_conduction.bundle_branch_block.incomplete_rbbb}
                      onChange={(e) => handleNestedInputChange('rhythm_and_conduction', 'bundle_branch_block', 'incomplete_rbbb', e.target.checked)}
                    />
                    Incomplete RBBB
                  </RadioLabel>
                </RadioGroup>
              </FormField>
              <FormField>
                <FormLabel>Other Findings</FormLabel>
                <RadioGroup>
                  <RadioLabel>
                    <RadioInput
                      type="checkbox"
                      checked={data.rhythm_and_conduction.other_findings.pacemaker_activity}
                      onChange={(e) => handleNestedInputChange('rhythm_and_conduction', 'other_findings', 'pacemaker_activity', e.target.checked)}
                    />
                    Pacemaker Activity
                  </RadioLabel>
                  <RadioLabel>
                    <RadioInput
                      type="checkbox"
                      checked={data.rhythm_and_conduction.other_findings.q_wave_presence}
                      onChange={(e) => handleNestedInputChange('rhythm_and_conduction', 'other_findings', 'q_wave_presence', e.target.checked)}
                    />
                    Q Wave Presence
                  </RadioLabel>
                </RadioGroup>
              </FormField>
            </FieldsContainer>
          </ContentBox>
        </SectionContainer>
      </SectionsRow>

      <SectionContainer>
        <SectionTitle>ADDITIONAL REMARKS</SectionTitle>
        <ContentBox>
          <FormField style={{ width: '100%' }}>
            <FormLabel>Additional Remarks</FormLabel>
            <FormTextarea
              value={data.additional_remarks || ''}
              onChange={(e) => setData({ ...data, additional_remarks: e.target.value })}
              placeholder="Enter any additional remarks or observations"
            />
          </FormField>
        </ContentBox>
      </SectionContainer>

      <SaveButton onClick={handleSave}>Save and Next</SaveButton>

      <SuccessModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title="ECG Data Saved"
        message={`ECG data has been successfully saved for Participant ID: ${participantId}`}
      />
    </ECGFormContainer>
  );
};

export default ECGForm;
